import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { trackMetaTrialInitiateCheckout } from "@/lib/meta-pixel";
import { useState, useEffect, useRef, type ReactNode } from "react";
import {
  Shield,
  MousePointer,
  Phone,
  Star,
  DollarSign,
  TrendingDown,
  Eye,
  ChevronRight,
  Check,
  ArrowRight,
  Zap,
  Quote,
  Play,
  X,
} from "lucide-react";
import logoBlack from "@assets/TORK_Logo_Black_1779647609157.png";
import logoWhite from "@assets/TORK_Logo_White_1779647609157.png";
import { TestimonialsSection, SectionCTA } from "@/components/marketing/social-proof";

// Fire the "play the VSL" event that <HomepageVsl /> listens for (scroll + unmute).
const playVsl = () => window.dispatchEvent(new Event("tork:play-vsl"));

// Optional copy overrides for the hero. Every field defaults to the homepage
// copy, so passing nothing leaves the homepage / other trial pages untouched.
// Only the /meta-trial landing page currently overrides these.
export type HeroCopy = {
  badge?: ReactNode;
  headline?: ReactNode;
  subhead?: ReactNode;
  ctaLabel?: string;
  priceLine?: ReactNode;
  whatYouGet?: string[];
};

export function NavBar() {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#0A0A0F]/90 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-6 h-40 flex items-center justify-between">
        <div className="flex items-center">
          <img src={logoBlack} alt="Tork" className="h-36 block dark:hidden" data-testid="logo-navbar" />
          <img src={logoWhite} alt="Tork" className="h-36 hidden dark:block" />
        </div>
        <div className="hidden md:flex items-center gap-8">
          <a href="#how-it-works" className="text-sm text-gray-400 hover:text-white transition-colors" data-testid="nav-how-it-works">How It Works</a>
          <Link href="/testimonials">
            <span className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer" data-testid="nav-testimonials">Testimonials</span>
          </Link>
          <a href="#pricing" className="text-sm text-gray-400 hover:text-white transition-colors" data-testid="nav-pricing">Pricing</a>
          <Link href="/login">
            <span className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer" data-testid="nav-login">Login</span>
          </Link>
          <Link href="/login">
            <Button onClick={trackMetaTrialInitiateCheckout} className="bg-[#F6642A] hover:bg-[#F6642A]/90 text-white font-semibold h-9 px-5" data-testid="nav-cta-trial">
              Start $1 Trial
            </Button>
          </Link>
        </div>
        <div className="md:hidden">
          <Link href="/login">
            <Button onClick={trackMetaTrialInitiateCheckout} className="bg-[#F6642A] hover:bg-[#F6642A]/90 text-white font-semibold h-9 px-4" data-testid="nav-cta-trial-mobile">
              Start $1 Trial
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
}

export function HeroSection({
  onWatchTour = playVsl,
  badge,
  headline,
  subhead,
  ctaLabel = "Start $1 Trial",
  priceLine,
  whatYouGet,
}: { onWatchTour?: () => void } & HeroCopy) {
  return (
    <section className="pt-32 pb-20 px-6 relative overflow-hidden" data-testid="section-hero">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#F6642A]/8 via-transparent to-transparent" />
      <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-[#F6642A]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#0081DF]/5 rounded-full blur-[100px]" />

      <div className="max-w-5xl mx-auto text-center relative z-10">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#F6642A]/20 bg-[#F6642A]/5 text-sm text-[#F6642A] font-medium mb-8" data-testid="badge-hero">
          <Zap className="w-3.5 h-3.5" />
          {badge ?? "Local business owners: AI your competitors don't have yet"}
        </div>

        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 tracking-tight" data-testid="text-hero-h1">
          {headline ?? (
            <>
              Your Google listing is either printing money or feeding your competitors.{" "}
              <span className="text-[#F6642A] font-bold">Turn it into a 24/7 lead machine.</span>
            </>
          )}
        </h1>

        <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed">
          {subhead ?? (
            <>
              Digital Domination is a Maps SEO platform that uses 35+ years of local marketing data, playbooks from 5,000+ businesses, and always&#8209;on AI to outrank nearby competitors, drive more calls, and cut your ad spend waste &ndash; without learning SEO or touching your website.
            </>
          )}
        </p>

        {whatYouGet && whatYouGet.length > 0 && (
          <ul className="flex flex-col items-center gap-3 max-w-xl mx-auto mb-10 text-left" data-testid="list-hero-benefits">
            {whatYouGet.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-base text-gray-300" data-testid={`benefit-hero-${i}`}>
                <Check className="w-5 h-5 text-[#F6642A] shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
          <Link href="/login">
            <Button onClick={trackMetaTrialInitiateCheckout} className="bg-[#F6642A] hover:bg-[#F6642A]/90 text-white font-semibold h-13 px-8 text-base" data-testid="cta-primary">
              {ctaLabel}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Button
            variant="outline"
            className="border-white/10 text-white hover:bg-white/5 h-13 px-8 text-base"
            data-testid="cta-secondary"
            onClick={onWatchTour}
          >
            <Play className="w-4 h-4 mr-2" />
            Watch a 3-minute product tour
          </Button>
        </div>

        {priceLine && (
          <p className="text-sm text-gray-400 mb-10" data-testid="text-hero-price-mechanic">
            {priceLine}
          </p>
        )}
        {!priceLine && <div className="mb-6" />}

        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
          <span className="flex items-center gap-1.5">
            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
            Trusted by 5,000+ local businesses
          </span>
          <span className="w-1 h-1 rounded-full bg-gray-600 hidden sm:block" />
          <span>35+ years of local SEO experience</span>
          <span className="w-1 h-1 rounded-full bg-gray-600 hidden sm:block" />
          <span>AI optimizing your listing 24/7</span>
        </div>

      </div>
    </section>
  );
}

export function ProblemSection() {
  const painPoints = [
    {
      icon: <TrendingDown className="w-6 h-6" />,
      emoji: "📉",
      title: "You're invisible in half your service area",
      body: "You might be ranking #1 downtown but #12 in the neighborhoods 10 minutes away. Those customers? Going to someone else.",
    },
    {
      icon: <Phone className="w-6 h-6" />,
      emoji: "📞",
      title: "You have no idea what Google is actually worth to you",
      body: "Calls, clicks, directions — it all adds up to real revenue. Most business owners have never seen that number.",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      emoji: "🛡️",
      title: "Anyone can mess with your Google listing",
      body: "Competitors, spammers, even random Google users can suggest edits to your listing. Most business owners find out after the damage is done.",
    },
  ];

  return (
    <section className="py-20 px-6 relative" data-testid="section-problem">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#F6642A]" data-testid="label-problem">THE PROBLEM</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-4 max-w-3xl mx-auto" data-testid="text-problem-h2">
            Right now, a competitor in your city is getting calls that should be yours.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {painPoints.map((p, i) => (
            <div
              key={i}
              className="p-6 rounded-2xl bg-[#12121A] border border-[#1E1E2E] hover:border-[#F6642A]/20 transition-colors"
              data-testid={`card-pain-${i}`}
            >
              <div className="text-3xl mb-4">{p.emoji}</div>
              <h3 className="text-lg font-bold text-white mb-2">{p.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
        <SectionCTA testid="cta-problem" body="Stop handing calls to your competitors. See where you really rank — start your $1 trial." />
      </div>
    </section>
  );
}

export function SolutionSection() {
  const features = [
    {
      label: "CLICKS",
      icon: <MousePointer className="w-6 h-6 text-[#F6642A]" />,
      headline: "Get found before your competitors do",
      body: "Our AI analyzes your market, finds every keyword customers use to find businesses like yours, and optimizes your Google listing to rank at the top — across your entire service area, not just one zip code.",
      metric: "Avg. 3–5× more profile views within 90 days",
    },
    {
      label: "CALLS",
      icon: <Phone className="w-6 h-6 text-[#F6642A]" />,
      headline: "Turn Google visibility into a ringing phone",
      body: "Track every call, click, and direction request your listing generates — and see it translated into real dollar value based on your average job. Know exactly what Google is worth to your business every single day.",
      metric: "Avg. 2–4× more calls in the first 6 months",
    },
    {
      label: "CUSTOMERS",
      icon: <Star className="w-6 h-6 text-[#F6642A]" />,
      headline: "Win the review battle. Win the customer.",
      body: "Aria responds to reviews in your voice, automatically. She requests new reviews from happy customers. She flags fake ones for removal. The result: a review profile that makes customers choose you over everyone else.",
      metric: "Avg. 4.7★ rating across our client base",
    },
    {
      label: "CASH",
      icon: <DollarSign className="w-6 h-6 text-[#F6642A]" />,
      headline: "See your Google ROI in actual dollars",
      body: "Every morning when you open your dashboard, you'll see one number: how much revenue Google generated for your business. Not impressions. Not rankings. Dollars — based on your average job value and close rate.",
      metric: "Avg. $8,400/month in tracked revenue opportunities",
    },
  ];

  return (
    <section className="py-20 px-6 relative" data-testid="section-solution">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#F6642A]" data-testid="label-solution">THE SOLUTION</span>
          <h2 className="text-3xl md:text-4xl font-bold text-white mt-4" data-testid="text-solution-h2">
            Clicks. Calls. Customers. Cash.
          </h2>
          <p className="text-gray-400 mt-3 max-w-xl mx-auto">
            Everything we do connects directly to one of these four outcomes. Here's how.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {features.map((f, i) => (
            <div
              key={i}
              className="p-6 sm:p-8 rounded-2xl bg-[#12121A] border border-[#1E1E2E] hover:border-[#F6642A]/20 transition-colors group"
              data-testid={`card-solution-${f.label.toLowerCase()}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#F6642A]/10 flex items-center justify-center">
                  {f.icon}
                </div>
                <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#F6642A]">{f.label}</span>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{f.headline}</h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-4">{f.body}</p>
              <div className="px-3 py-2 rounded-lg bg-[#F6642A]/5 border border-[#F6642A]/10 inline-block">
                <span className="text-xs font-semibold text-[#F6642A]">{f.metric}</span>
              </div>
            </div>
          ))}
        </div>
        <SectionCTA testid="cta-solution" body="Clicks, calls, customers, cash — put it all on autopilot for $1." />
      </div>
    </section>
  );
}

export function HowItWorksSection() {
  const steps = [
    {
      num: "1",
      title: "Connect your Google listing",
      body: "We pull your business info straight from Google. Answer 5 quick questions about your jobs and customers. Done.",
    },
    {
      num: "2",
      title: "We map your market",
      body: "Our AI scans your rankings, analyzes your competitors, and identifies every opportunity to steal market share in your area.",
    },
    {
      num: "3",
      title: "Aria goes to work",
      body: "Posts. Review responses. Listing protection. Citation building. Aria handles it all — automatically — and reports back in plain English.",
    },
  ];

  return (
    <section id="how-it-works" className="py-20 px-6 relative" data-testid="section-how-it-works">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-white" data-testid="text-how-h2">
            Set up in 5 minutes. Aria handles everything else.
          </h2>
          <p className="text-gray-400 mt-3 max-w-2xl mx-auto">
            We built the world's most automated Google Business Profile platform so you can focus on running your business.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((s, i) => (
            <div key={i} className="text-center" data-testid={`card-step-${i}`}>
              <div className="w-14 h-14 rounded-full bg-[#F6642A]/10 border-2 border-[#F6642A]/30 flex items-center justify-center mx-auto mb-5">
                <span className="text-xl font-bold text-[#F6642A]">{s.num}</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{s.title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
        <SectionCTA testid="cta-how-it-works" body="Set up in 5 minutes. Let Aria do the rest — start your $1 trial." />
      </div>
    </section>
  );
}

// The hero VSL (value/sales video). Self-contained: autoplays muted, and when
// it receives the global "tork:play-vsl" event (fired by hero "watch tour"
// buttons) it scrolls itself into view, unmutes, and plays via the Vimeo API.
export function HomepageVsl() {
  const vslWrapRef = useRef<HTMLDivElement>(null);
  const vslIframeRef = useRef<HTMLIFrameElement>(null);
  const vimeoPlayerRef = useRef<any>(null);
  const vslTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getVimeoPlayer = async () => {
    if (vimeoPlayerRef.current) return vimeoPlayerRef.current;
    const w = window as any;
    if (!w.Vimeo) {
      await new Promise<void>((resolve, reject) => {
        const existing = document.querySelector(
          'script[src="https://player.vimeo.com/api/player.js"]',
        ) as HTMLScriptElement | null;
        if (existing) {
          if (w.Vimeo) return resolve();
          existing.addEventListener("load", () => resolve());
          existing.addEventListener("error", () => reject(new Error("vimeo")));
          return;
        }
        const s = document.createElement("script");
        s.src = "https://player.vimeo.com/api/player.js";
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("vimeo"));
        document.body.appendChild(s);
      });
    }
    if (w.Vimeo && vslIframeRef.current) {
      vimeoPlayerRef.current = new w.Vimeo.Player(vslIframeRef.current);
    }
    return vimeoPlayerRef.current;
  };

  useEffect(() => {
    const onPlay = () => {
      vslWrapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (vslTimerRef.current) clearTimeout(vslTimerRef.current);
      vslTimerRef.current = setTimeout(async () => {
        try {
          const player = await getVimeoPlayer();
          if (player) {
            await player.setMuted(false);
            await player.play();
          }
        } catch {}
      }, 600);
    };
    window.addEventListener("tork:play-vsl", onPlay);
    return () => {
      window.removeEventListener("tork:play-vsl", onPlay);
      if (vslTimerRef.current) clearTimeout(vslTimerRef.current);
      try {
        vimeoPlayerRef.current?.destroy?.();
      } catch {}
    };
  }, []);

  return (
    <div ref={vslWrapRef} className="max-w-4xl mx-auto px-6 -mt-6 mb-10 relative z-10" data-testid="vsl-video-wrapper">
      <div
        className="w-full rounded-2xl border border-[#1E1E2E] shadow-2xl shadow-[#F6642A]/10 overflow-hidden"
        style={{ padding: "56.25% 0 0 0", position: "relative" }}
      >
        <iframe
          ref={vslIframeRef}
          src="https://player.vimeo.com/video/1198390423?badge=0&autopause=0&player_id=0&app_id=58479&autoplay=1&muted=1"
          frameBorder="0"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
          title="Local Business Owners: Get Into Google Maps' Top 3 For $1 - Tork Powered By Digital Domination"
          data-testid="vsl-video"
        />
      </div>
    </div>
  );
}

export function PricingSection() {
  const plans = [
    {
      name: "Recon",
      price: "$1",
      tagline: "See where you stand before you attack.",
      features: [
        "Scan 1 location and see where you rank on Google Maps",
        "Spot which competitors are beating you and on which blocks",
        "Basic geo-grid map of your true visibility",
        "Upgrade anytime to start fixing issues automatically",
      ],
      cta: "Start for $1 →",
      highlight: false,
    },
    {
      name: "Local Domination",
      price: "$399",
      tagline: "Full autopilot for 1 location.",
      features: [
        "Full autopilot for 1 Google Business Profile",
        "AI-written posts shipped every week",
        "Review request + auto-reply engine",
        "Aria AI assistant + listing protection",
        "Monthly plain-English report",
      ],
      cta: "Start $1 / 3-day trial →",
      highlight: true,
      badge: "Most Popular Starter",
    },
    {
      name: "Multi-Location Domination",
      price: "$599",
      tagline: "Consistent leads across 2–3 locations.",
      features: [
        "Everything in Local Domination, plus:",
        "Up to 3 locations from one dashboard",
        "Multi-location reporting",
        "Shared on-brand review templates",
        "Product Forge for GBP product cards",
      ],
      cta: "Start $1 / 3-day trial →",
      highlight: false,
      badge: "Best Value",
    },
    {
      name: "Market Monopoly",
      price: "$999",
      tagline: "Own your entire service area.",
      features: [
        "Everything in Multi-Location Domination, plus:",
        "Up to 5 locations included",
        "Priority support",
        "White-glove onboarding per location",
        "Extra locations $175 / 28 days each",
      ],
      cta: "Start $1 / 3-day trial →",
      highlight: false,
    },
  ];

  return (
    <section id="pricing" className="py-20 px-6 relative" data-testid="section-pricing">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-bold text-white" data-testid="text-pricing-h2">
            Less than what you make on one job. For unlimited leads.
          </h2>
          <p className="text-gray-400 mt-3 max-w-2xl mx-auto">
            Traditional agencies charge $2,000&ndash;$5,000/month and make you wait 6 months to see results. Our AI does it better. Starting at $399 every 28 days.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((plan, i) => (
            <div
              key={i}
              className={`p-6 rounded-2xl border flex flex-col ${
                plan.highlight
                  ? "bg-[#12121A] border-[#F6642A]/40 ring-1 ring-[#F6642A]/20"
                  : "bg-[#12121A] border-[#1E1E2E]"
              }`}
              data-testid={`card-plan-${plan.name.toLowerCase()}`}
            >
              {plan.badge && (
                <div className="inline-flex self-start px-2.5 py-0.5 rounded-full bg-[#F6642A]/10 text-[#F6642A] text-xs font-semibold mb-3">
                  {plan.badge}
                </div>
              )}
              <h3 className="text-lg font-bold text-white">{plan.name}</h3>
              <div className="flex items-baseline gap-1 mt-2 mb-1">
                <span className="text-3xl font-bold text-white">{plan.price}</span>
                <span className="text-sm text-gray-500">/ 28 days</span>
              </div>
              <p className="text-sm text-gray-400 mb-5">{plan.tagline}</p>
              <ul className="space-y-2 mb-6 flex-1">
                {plan.features.map((f, fi) => (
                  <li key={fi} className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="w-4 h-4 text-[#F6642A] shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link href="/login">
                <Button
                  onClick={trackMetaTrialInitiateCheckout}
                  className={`w-full font-semibold ${
                    plan.highlight
                      ? "bg-[#F6642A] hover:bg-[#F6642A]/90 text-white"
                      : "bg-white/5 hover:bg-white/10 text-white border border-white/10"
                  }`}
                  data-testid={`cta-plan-${plan.name.toLowerCase()}`}
                >
                  {plan.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-gray-500 mt-8">
          $1 for 3 days, then your plan price. No contracts, no setup fees, cancel in 2 clicks.
        </p>
      </div>
    </section>
  );
}

export function FinalCTASection() {
  return (
    <section className="py-24 px-6 relative" data-testid="section-final-cta">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-[#F6642A]/8 via-transparent to-transparent" />
      <div className="max-w-3xl mx-auto text-center relative z-10">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="text-final-h2">
          Your competitors are getting smarter about Google. Are you?
        </h2>
        <p className="text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed">
          Every day you wait, a competitor in your city gets more reviews, more rankings, and more calls. Start your $1 trial and see where you stand right now &mdash; in 2 minutes.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
          <Link href="/login">
            <Button onClick={trackMetaTrialInitiateCheckout} className="bg-[#F6642A] hover:bg-[#F6642A]/90 text-white font-semibold h-13 px-10 text-base" data-testid="cta-final-primary">
              Start $1 Trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link href="/login">
            <Button variant="ghost" className="text-gray-400 hover:text-white" data-testid="cta-final-login">
              Or log in to your account <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-gray-500">
          <span>Just $1 to get started</span>
          <span className="w-1 h-1 rounded-full bg-gray-600" />
          <span>Cancel anytime</span>
          <span className="w-1 h-1 rounded-full bg-gray-600" />
          <span>5,000+ businesses trust DD</span>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-white/5 py-10 px-6" data-testid="section-footer">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <img src={logoBlack} alt="Tork" className="h-32 block dark:hidden" data-testid="logo-footer" />
            <img src={logoWhite} alt="Tork" className="h-32 hidden dark:block" />
            <div>
              <span className="text-sm font-bold text-white sr-only">Digital Domination</span>
              <p className="text-xs text-gray-500">Clicks. Calls. Customers. Cash.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
            <a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <Link href="/login"><span className="hover:text-white transition-colors cursor-pointer">Login</span></Link>
            <a
              href="https://digitaldomination.ai/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
              data-testid="link-privacy"
            >
              Privacy Policy
            </a>
            <a
              href="https://digitaldomination.ai/terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
              data-testid="link-terms"
            >
              Terms of Service
            </a>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-white/5 text-center text-xs text-gray-600">
          &copy; 2026 Digital Domination. 35+ years of experience. 5,000+ businesses helped.
        </div>
      </div>
    </footer>
  );
}


export default function HomePage() {
  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white">
      <NavBar />
      <HeroSection
        onWatchTour={playVsl}
        headline={
          <>
            <span className="text-[#F6642A] font-bold">Tork</span>: AI Local SEO Software that turns your Google listing into a 24/7 lead machine.
          </>
        }
        subhead={
          <>
            Tork is the AI local SEO platform from Digital Domination &ndash; built on 35+ years of local marketing data and playbooks from 5,000+ businesses to outrank nearby competitors, drive more calls, and cut wasted ad spend, without learning SEO or touching your website.
          </>
        }
      />
      <HomepageVsl />
      <ProblemSection />
      <SolutionSection />
      <HowItWorksSection />
      <TestimonialsSection />
      <PricingSection />
      <FinalCTASection />
      <Footer />
    </div>
  );
}
